import React, { useState, useEffect, useMemo } from 'react';
import { formatMoney } from '../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { X, Minus, Plus, Trash2, Search, Loader2, Pencil, AlertTriangle, ArrowLeft, Home, ShoppingCart } from 'lucide-react';
import { API_BASE_URL } from './config/api';
import { productsAPI } from '../services/api';
import type { Product, Category } from '../types/order';
import { defaultSize, isFeeProduct, isSizeSellable, lineUnitPrice, productDisplayPrice, sortSizes } from '../utils/pricing';
import { lineNameWithSize } from '../utils/sellableSizes';
import ProductCatalog from './shared/ProductCatalog';
import TouchKeyboard from './ui/TouchKeyboard';

/**
 * Edit the items of an ACCEPTED platform order before its receipt is issued —
 * the store agreed a replacement with the customer over the phone and the
 * receipt (generated at completion from orders_products) must match reality.
 *
 * POS-style: the store's catalog is browsable on the left (categories → grid,
 * same shared component as the take-away POS) and the cart — pre-loaded with
 * the order's current items — is on the right. Tap a product to add it, adjust
 * quantity, tap a price to change it, remove lines. On save the item list is
 * pushed to PUT /orders/:id/items (platform APIs don't support item edits, so
 * this is local-only; the backend keeps an audit snapshot in order_json).
 *
 * "Simple add" by design: a tapped product is added as a plain line at its
 * delivery price — no sizes/options/ingredients (kept fast for phone swaps).
 *
 * Εξαίρεση (FE-14, απόφαση 12_server_floor_no_size): η τιμή ζει στο μέγεθος, άρα η νέα
 * γραμμή κουβαλά ΚΑΙ internal_size_id = το προεπιλεγμένο μέγεθος — αυτό που ήδη δείχνει
 * το πλακίδιο και χρεώνεται (productDisplayPrice). Χωρίς αυτό η γραμμή δεν ξέρει τι
 * πουλήθηκε. Κανένα ποσό δεν αλλάζει· γράφεται μόνο η ταυτότητα. Τέλος: ποτέ μέγεθος.
 *
 * Ε2(α) (ιδιοκτήτης 1/10/2026): αν το προϊόν έχει >1 πωλήσιμα μεγέθη, η νέα γραμμή μπαίνει
 * πάλι με το προεπιλεγμένο (ίδιο ποσό με πριν) και δείχνει τα μεγέθη ως επιλογές· το ποσό
 * αλλάζει ΜΟΝΟ αν ο ταμίας διαλέξει άλλο μέγεθος (lineUnitPrice).
 *
 * P5-E19 (ιδιοκτήτης 2/10/2026): η γραμμή κρατά μόνο όνομα για απόδειξη/κουζίνα/λίστα, άρα το
 * μέγεθος γράφεται στο όνομα («Κουλούρι Μεγάλο») με τον κανόνα του χαρτιού (lineNameWithSize):
 * το μοναδικό πωλήσιμο μέγεθος και τα τέλη ποτέ. Μόνο νέες γραμμές· κανένα ποσό δεν αλλάζει.
 */

interface EditableItem {
  key: string;               // stable local key (existing rows use their DB id)
  id?: number;               // orders_products.id when the row already exists
  internal_product_id?: number | null;
  // Μόνο σε ΝΕΑ γραμμή (όχι τέλους): το προεπιλεγμένο μέγεθος (defaultSize).
  // Οι υπάρχουσες γραμμές δεν το έχουν ποτέ — ο backend δεν τις ξαναγράφει.
  internal_size_id?: number | null;
  // Ε2(α): τα πωλήσιμα μεγέθη με την τιμή delivery τους, μόνο όταν είναι >1. Κρατιούνται
  // στη γραμμή (όχι από τον κατάλογο) ώστε η επιλογή να μην εξαρτάται από επαναφόρτωση.
  // line_name: το όνομα της γραμμής με αυτό το μέγεθος (P5-E19), υπολογισμένο μία φορά εδώ.
  sizeOptions?: Array<{ id: number; name: string; unit_price: number; line_name: string }>;
  product_name: string;
  quantity: number;
  unit_price: number;        // gross €
  product_notes?: string | null;
}

interface EditOrderItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  storeId: number;
  orderItems: Array<{ id: number; product_name: string; quantity: number; unit_price: number; product_notes: string | null }>;
  onSaved: (newTotal: number) => void;
}

const EditOrderItemsModal: React.FC<EditOrderItemsModalProps> = ({ isOpen, onClose, order, storeId, orderItems, onSaved }) => {
  const { t } = useTranslation('orders');
  const [items, setItems] = useState<EditableItem[]>([]);

  // Catalog (left pane)
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [currentView, setCurrentView] = useState<'categories' | 'subcategories' | 'products'>('categories');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // kb 'price' keeps raw cent digits (POS-style: typing 3→5→0 shows €3.50).
  const [kb, setKb] = useState<{ type: 'price'; key: string; raw: string } | { type: 'search' } | null>(null);

  // Pre-fill the cart from the order's current items whenever the modal opens.
  // ΣΗΜΕΙΩΣΗ (docs/platform_name_mapping.md §9): οι υπάρχουσες γραμμές ΔΕΝ
  // κουβαλούν εδώ internal_product_id — ούτε χρειάζεται. Το PUT /orders/:id/items
  // ενημερώνει τις γραμμές με id μόνο σε όνομα/ποσότητα/τιμή/σχόλια, οπότε
  // internal_product_id, vat και is_mapped (άρα και η αντιστοίχιση/ο ΦΠΑ της
  // απόδειξης) μένουν ανέπαφα. Αν κάποτε σταλεί εδώ internal_product_id, ο
  // backend το αγνοεί για τις υπάρχουσες γραμμές — ΔΕΝ τις ξαναγράφει.
  useEffect(() => {
    if (isOpen) {
      setItems(orderItems.map(p => ({
        key: `db-${p.id}`,
        id: p.id,
        product_name: p.product_name,
        quantity: p.quantity,
        unit_price: parseFloat(String(p.unit_price)) || 0,
        product_notes: p.product_notes
      })));
      setError(null);
      setKb(null);
      setSearchTerm('');
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setCurrentView('categories');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load the store catalog once when opened.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      try {
        const [cats, prods] = await Promise.all([
          productsAPI.getCategories(storeId),
          productsAPI.getProducts(storeId, { available_only: true }),
        ]);
        if (cancelled) return;
        setCategories((cats || []).filter((c: Category) => c.is_active));
        setProducts(prods || []);
      } catch {
        if (!cancelled) setError(t('edit.loadCatalogFailed'));
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, storeId]);

  const catalogPrice = (p: Product) => productDisplayPrice(p, 'delivery');

  const originalTotal = parseFloat(String(order?.total_amount)) || 0;
  const newTotal = useMemo(
    () => parseFloat(items.reduce((s, i) => s + i.quantity * i.unit_price, 0).toFixed(2)),
    [items]
  );
  const itemCount = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  // ── Catalog navigation (mirrors the take-away POS) ──
  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category.id);
    setSearchTerm('');
    if (category.subcategories && category.subcategories.length > 0) {
      setCurrentView('subcategories');
    } else {
      setSelectedSubcategory(null);
      setCurrentView('products');
    }
  };
  const handleSubcategorySelect = (subcategory: Category | null) => {
    setSelectedSubcategory(subcategory?.id || null);
    setSearchTerm('');
    setCurrentView('products');
  };
  const handleBack = () => {
    if (currentView === 'products') {
      const category = categories.find(c => c.id === selectedCategory);
      if (category?.subcategories && category.subcategories.length > 0) {
        setCurrentView('subcategories');
      } else {
        setSelectedCategory(null);
        setCurrentView('categories');
      }
    } else if (currentView === 'subcategories') {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setCurrentView('categories');
    }
  };
  const handleHome = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm('');
    setCurrentView('categories');
  };

  // Tap a product → add a plain line, or bump the quantity of the same product
  // if it's already in the cart (matched by the catalog product id AND size).
  const addProduct = (p: Product) => {
    // Το μέγεθος που τιμολογεί ήδη το πλακίδιο (defaultSize της §3.3)· τέλος → κανένα.
    // Αν κανένα μέγεθος δεν είναι πωλήσιμο, δεν στέλνουμε τίποτα και διαλέγει ο server
    // (BE-09), ώστε να μην προτείνουμε κρυφό μέγεθος.
    const sized = !isFeeProduct(p);
    const chosen = sized ? defaultSize(p.sizes) : null;
    const sizeId = chosen?.id ?? null;
    const sellable = sized ? sortSizes((p.sizes || []).filter(isSizeSellable)) : [];
    const sizeOptions = sellable.length > 1
      ? sellable.map(s => ({ id: s.id, name: s.name, unit_price: lineUnitPrice(p, s, 'delivery'), line_name: lineNameWithSize(p.name, p, s) }))
      : undefined;
    setItems(prev => {
      // Συγχώνευση και κατά μέγεθος: ίδιο προϊόν με άλλο μέγεθος είναι άλλη γραμμή.
      const idx = prev.findIndex(i =>
        i.internal_product_id === p.id && (i.internal_size_id ?? null) === sizeId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(99, next[idx].quantity + 1) };
        return next;
      }
      return [...prev, {
        key: `new-${p.id}-${prev.length}-${prev.reduce((m, i) => Math.max(m, i.quantity), 0)}`,
        internal_product_id: p.id,
        internal_size_id: sizeId,
        sizeOptions,
        // P5-E19: «Κουλούρι Μεγάλο» όπου το μέγεθος φαίνεται και στο χαρτί· μονομεγέθες/τέλος → σκέτο.
        product_name: lineNameWithSize(p.name, p, chosen),
        quantity: 1,
        unit_price: parseFloat(catalogPrice(p).toFixed(2)),
        product_notes: null,
      }];
    });
  };

  const setQty = (key: string, delta: number) => {
    setItems(prev => prev.map(i => i.key === key
      ? { ...i, quantity: Math.min(99, Math.max(1, i.quantity + delta)) }
      : i));
  };
  // Ε2(α): άλλο μέγεθος → η τιμή ΕΚΕΙΝΟΥ του μεγέθους, και ας είχε αλλαχτεί η τιμή με
  // το χέρι (ρητή ενέργεια του ταμία). Το πληκτρολόγιο τιμής κλείνει, για να μη γράψει
  // μετά τα παλιά ψηφία πάνω στη νέα τιμή. Το ήδη επιλεγμένο μέγεθος δεν κάνει τίποτα:
  // αλλιώς ένα δεύτερο πάτημα θα έσβηνε σιωπηλά τη χειροκίνητη τιμή χωρίς αλλαγή μεγέθους.
  const setLineSize = (key: string, sizeId: number) => {
    const line = items.find(i => i.key === key);
    if (!line || line.internal_size_id === sizeId) return;
    setItems(prev => prev.map(i => {
      if (i.key !== key) return i;
      const opt = i.sizeOptions?.find(o => o.id === sizeId);
      return opt ? { ...i, internal_size_id: opt.id, unit_price: opt.unit_price, product_name: opt.line_name } : i;
    }));
    setKb(prev => (prev?.type === 'price' && prev.key === key ? null : prev));
  };

  const removeItem = (key: string) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter(i => i.key !== key));
    setKb(null);
  };
  const openPriceKb = (item: EditableItem) => {
    setKb({ type: 'price', key: item.key, raw: String(Math.round(item.unit_price * 100) || '') });
  };

  const kbValue = kb?.type === 'price' ? kb.raw : searchTerm;
  const kbChange = (next: string) => {
    if (!kb) return;
    if (kb.type === 'price') {
      const raw = next.replace(/\D/g, '').slice(0, 6);
      setKb({ ...kb, raw });
      const price = parseFloat(((parseInt(raw || '0', 10)) / 100).toFixed(2));
      setItems(prev => prev.map(i => i.key === kb.key ? { ...i, unit_price: price } : i));
    } else {
      setSearchTerm(next);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/orders/${order.id}/items`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          items: items.map(i => ({
            id: i.id,
            // Μόνο οι ΝΕΕΣ γραμμές (χωρίς id) κουβαλούν προϊόν καταλόγου: από
            // εκεί παίρνει ο backend τον σωστό ΦΠΑ. Οι παλιές γραμμές κρατούν
            // ό,τι αντιστοίχιση είχαν ήδη.
            internal_product_id: i.internal_product_id ?? undefined,
            // Το μέγεθος επίσης ΜΟΝΟ στις νέες γραμμές (BE-09 το επαληθεύει ότι ανήκει
            // στο προϊόν)· σε υπάρχουσα γραμμή δεν στέλνεται ποτέ.
            internal_size_id: i.id == null ? (i.internal_size_id ?? undefined) : undefined,
            product_name: i.product_name,
            quantity: i.quantity,
            unit_price: i.unit_price,
            product_notes: i.product_notes ?? null
          }))
        })
      });
      const data = await res.json();
      // Τα νέα σφάλματα του backend έρχονται ως {error:"<ελληνικά>", code} χωρίς message
      // (π.χ. 400 INVALID_SIZE όταν το μέγεθος σβήστηκε/ξαναφτιάχτηκε όσο ήταν ανοιχτό
      // το modal)· ο ταμίας πρέπει να δει το συγκεκριμένο μήνυμα, όχι το γενικό. Τα
      // παλιά σφάλματα (αγγλικό error + message) κρατούν τη σημερινή συμπεριφορά.
      if (!res.ok) throw new Error((data.code && data.error) || data.message || t('edit.saveFailed'));
      onSaved(data.new_total);
      onClose();
    } catch (e: any) {
      setError(e.message || t('edit.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !order) return null;

  return (
    // Full-screen overlay. stopPropagation keeps taps from bubbling to the
    // OrderPreviewModal backdrop (which closes on click) underneath.
    <div
      className="fixed inset-0 bg-surface-0 z-[60] flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="px-5 py-3 border-b border-line bg-surface-1 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500/15 rounded-xl flex items-center justify-center">
            <Pencil className="w-5 h-5 text-amber-700 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-t1">{t('edit.title')}</h2>
            <p className="text-xs text-t3">#{order.short_code || order.platform_order_id} · {order.platform_name}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-t3 hover:text-t2 p-2 rounded-lg hover:bg-surface-2">
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="flex-1 flex min-h-0">
        {/* ============== LEFT: catalog ============== */}
        <div className="flex-1 flex flex-col min-w-0 border-r border-line">
          {/* Catalog toolbar */}
          <div className="p-3 border-b border-line bg-surface-1/60 flex items-center gap-2 shrink-0">
            <button onClick={handleHome} className="p-2 rounded-lg bg-surface-2 text-t2 hover:bg-surface-3" title={t('edit.home', { defaultValue: 'Αρχή' })}>
              <Home className="w-5 h-5" />
            </button>
            <button
              onClick={handleBack}
              disabled={currentView === 'categories' && !searchTerm}
              className="p-2 rounded-lg bg-surface-2 text-t2 hover:bg-surface-3 disabled:opacity-30"
              title={t('common:back')}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-t3 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setKb({ type: 'search' })}
                placeholder={t('edit.searchProduct')}
                className="w-full h-10 pl-9 pr-3 rounded-lg bg-surface-2 dark:bg-surface-1 border border-line-strong text-t1 text-sm placeholder:text-t4"
              />
            </div>
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-3">
            <ProductCatalog
              categories={categories}
              products={products}
              loading={loadingCatalog}
              currentView={currentView}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              searchTerm={searchTerm}
              categoryColumns={3}
              productColumns={4}
              onCategorySelect={handleCategorySelect}
              onSubcategorySelect={handleSubcategorySelect}
              onProductClick={addProduct}
              onProductMouseDown={() => {}}
              onProductMouseUp={() => {}}
              getProductPrice={catalogPrice}
              translationNamespace="orders"
            />
          </div>
        </div>

        {/* ============== RIGHT: cart ============== */}
        <div className="cashier-basket w-[380px] bg-surface-0 flex flex-col shrink-0">
          <div className="p-3 border-b border-line bg-surface-1 flex items-center gap-2 shrink-0">
            <div className="cashier-basket-icon w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-t1" />
            </div>
            <div className="flex-1">
              <p className="text-t1 font-semibold text-sm">{t('edit.cart', { defaultValue: 'Καλάθι παραγγελίας' })}</p>
              <p className="text-t3 text-xs">{itemCount} {t('preview.items', { defaultValue: 'προϊόντα' })}</p>
            </div>
          </div>

          {/* Fiscal warning */}
          <div className="mx-3 mt-3 flex items-start gap-2 text-xs text-amber-800/90 dark:text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5 shrink-0">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{t('edit.platformWarning')}</span>
          </div>

          {/* Lines */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {items.map(item => (
              <div key={item.key} className="bg-surface-2 dark:bg-surface-1 border border-line rounded-xl p-3 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-t1 text-sm font-medium truncate">{item.product_name}</p>
                  {item.sizeOptions && (
                    <div className="mt-1 flex flex-wrap gap-1" role="group" aria-label={t('edit.size', { defaultValue: 'Μέγεθος' })}>
                      {item.sizeOptions.map(o => {
                        const selected = o.id === item.internal_size_id;
                        return (
                          <button
                            key={o.id}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => setLineSize(item.key, o.id)}
                            className={`text-xs px-2 py-0.5 rounded border ${
                              selected
                                ? 'border-amber-500 bg-amber-500/15 text-amber-800 dark:text-amber-300 font-semibold'
                                : 'border-line-strong text-t2 hover:border-slate-400'
                            }`}
                          >
                            {o.name} · {formatMoney(o.unit_price)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => openPriceKb(item)}
                    className={`mt-1 text-xs font-mono px-2 py-0.5 rounded border ${
                      kb?.type === 'price' && kb.key === item.key
                        ? 'border-cyan-500 text-cyan-700 dark:text-cyan-300 bg-surface-0'
                        : 'border-line-strong text-t2 hover:border-slate-400'
                    }`}
                  >
                    {t('edit.price')}: {formatMoney(item.unit_price)}
                  </button>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => setQty(item.key, -1)} disabled={item.quantity <= 1}
                    className="w-9 h-9 rounded-lg bg-surface-2 text-t1 disabled:opacity-30 active:bg-surface-3 flex items-center justify-center">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-7 text-center text-t1 font-bold">{item.quantity}</span>
                  <button type="button" onClick={() => setQty(item.key, 1)}
                    className="w-9 h-9 rounded-lg bg-surface-2 text-t1 active:bg-surface-3 flex items-center justify-center">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <span className="w-14 text-right text-t1 font-semibold text-sm shrink-0">
                  {formatMoney((item.quantity * item.unit_price))}
                </span>
                <button type="button" onClick={() => removeItem(item.key)} disabled={items.length <= 1}
                  title={items.length <= 1 ? t('edit.lastItem') : undefined}
                  className="w-9 h-9 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400 disabled:opacity-30 active:bg-red-500/25 flex items-center justify-center shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {error && (
            <div className="mx-3 mb-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-700 dark:text-red-300 text-sm shrink-0">{error}</div>
          )}

          {/* Footer */}
          <div className="px-4 py-3 border-t border-line bg-surface-1 shrink-0 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-t3">{t('edit.originalTotal')}: <span className="font-mono text-t2">{formatMoney(originalTotal)}</span></span>
              <span className={`font-bold text-lg font-mono ${Math.abs(newTotal - originalTotal) > 0.005 ? 'text-amber-800 dark:text-amber-300' : 'text-t1'}`}>
                {t('edit.newTotal')}: {formatMoney(newTotal)}
              </span>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} disabled={saving}
                className="flex-1 py-3 rounded-xl border border-line-strong text-t2 hover:bg-surface-2 font-medium">
                {t('edit.cancel')}
              </button>
              <button type="button" onClick={save} disabled={saving || items.length === 0}
                className="cashier-primary flex-[1.4] py-3 rounded-xl bg-amber-600 hover:bg-amber-700 active:bg-amber-700 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? (<><Loader2 className="w-4 h-4 animate-spin" /> {t('edit.saving')}</>) : t('edit.save')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {kb && (
        <TouchKeyboard
          mode={kb.type === 'price' ? 'numeric' : 'text'}
          label={kb.type === 'price'
            ? `${t('edit.price')} — ${formatMoney(((parseInt(kb.raw || '0', 10)) / 100))}`
            : t('edit.searchProduct')}
          value={kbValue}
          onChange={kbChange}
          onDone={() => setKb(null)}
          maxLength={kb.type === 'price' ? 6 : undefined}
        />
      )}
    </div>
  );
};

export default EditOrderItemsModal;
